// src/components/PropertyForm.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { submitProperty, getPropertyById, updateProperty } from '../api';
import {
    BasicDetails,
    OfficeDetails,
    WarehouseDetails,
    FinancialDetails,
    LandlordDetails,
    PhotoUpload
} from './PropertyFormSections';

// ✅ Initial form state (empty)
const EMPTY_FORM = {
    // Basic Details
    location: '',
    sub_locality: '',
    city: 'mumbai',
    property_type: 'Office Lease',
    latitude: '',
    longitude: '',

    // Financial Details
    price: '',
    carpet_area: '',
    leasable_area: '',
    car_parks: '',
    furnishing: 'N/A',
    price_per_sqft: '',
    deposit: '',
    pre_leased: '',

    // Landlord Details
    landlord_name: '',
    landlord_contact: '',

    // Office Details
    floor: '',
    chargeable_area: '',
    carpet_efficiency: '',
    floor_to_floor_height: '',
    floor_to_ceiling_height: '',
    interior_condition: '',
    rent_per_sqft: '',
    cam_charges: 'Included',
    security_deposit: '',
    security_deposit_terms: '',
    rent_free_period: '',
    rent_escalation: '',
    lock_in_period: '',
    notice_period: '',
    parking_details: '',
    additional_parking_cost: '',
    nearest_metro: '',
    distance_airport_t1: '',
    distance_airport_t2: '',
    nearest_hospital: '',
    nearest_police_station: '',
    nearest_fire_station: '',
    stamp_duty: '',
    registration_details: '',
    occupancy_certificate: '',
    handover_date: '',
    property_age: '',
    tenant_fitout_allowed: true,
    gst_applicable: true,
    gst_percentage: 18,

    // Warehouse Details
    ceiling_height: '',
    floor_load_capacity: '',
    number_of_docks: 0,
    dock_type: '',
    power_supply: '',
    water_supply: '',
    fire_safety: '',
    security_system: '',
    road_access: '',
    truck_parking: '',
    proximity_to_highway: '',
    proximity_to_airport: '',
    proximity_to_railway: '',
    is_air_conditioned: false,
    has_crane_facility: false,
    has_weighbridge: false,
    has_employee_facilities: false,
    has_office_space: false,
    plot_area: '',
    built_up_area: '',
    warehouse_area: '',
    description: '',

    // Common
    highlights: '',
    is_treta_exclusive: false,
};

function PropertyForm() {
    const { id } = useParams();  // ✅ Get ID from URL (edit mode)
    const navigate = useNavigate();

    const isEditMode = Boolean(id);

    const [formData, setFormData] = useState(EMPTY_FORM);
    const [photo, setPhoto] = useState(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);  // For data fetch
    const [saving, setSaving] = useState(false);     // For submit

    const userRole = localStorage.getItem('role');
    const isAdmin = userRole === 'admin';

    // ===== LOAD EXISTING DATA (edit mode only) =====
    useEffect(() => {
        if (!isEditMode) return;

        const fetchProperty = async () => {
            try {
                setLoading(true);
                setError('');

                const data = await getPropertyById(id);

                // Merge into form, keeping only known fields
                const merged = { ...EMPTY_FORM };
                Object.keys(EMPTY_FORM).forEach((key) => {
                    if (data[key] !== undefined && data[key] !== null) {
                        merged[key] = data[key];
                    }
                });

                setFormData(merged);
            } catch (err) {
                const msg = err.response?.data?.error || err.message || 'Failed to load property';
                setError(msg);
                if (err.response?.status === 404) {
                    setTimeout(() => navigate('/my-properties'), 2000);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchProperty();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEditMode]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handlePhotoChange = (e) => {
        setPhoto(e.target.files[0]);
    };

    const handleLocationSelect = (lat, lng) => {
        setFormData({
            ...formData,
            latitude: lat,
            longitude: lng,
        });
    };

    const isWarehouseType = () => {
        const warehouseTypes = [
            'Warehouse Rent', 'Warehouse Sale', 'Industrial Land',
            'Factory', 'Godown', 'Cold Storage', 'Logistics Park'
        ];
        return warehouseTypes.includes(formData.property_type);
    };

    const isOfficeType = () => {
        return ['Office Lease', 'Office Sale'].includes(formData.property_type);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');
        setSaving(true);

        const data = new FormData();
        for (const key in formData) {
            if (formData[key] !== undefined && formData[key] !== null) {
                data.append(key, formData[key]);
            }
        }
        if (photo) {
            data.append('photo', photo);
        }

        try {
            let response;
            if (isEditMode) {
                // Edit mode: use PUT
                response = await updateProperty(id, data);
                setMessage(response.message || 'Property updated successfully');
                setTimeout(() => {
                    navigate('/my-properties');
                }, 1500);
            } else {
                // Submit mode: use POST
                response = await submitProperty(data);
                setMessage(response.message);
                setTimeout(() => {
                    navigate('/');
                }, 1500);
            }
        } catch (err) {
            if (err.response?.status === 401) {
                setError('Please log in to submit a property');
                setTimeout(() => navigate('/login'), 1000);
            } else {
                setError(err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'submit'} property`);
            }
        } finally {
            setSaving(false);
        }
    };

    // Loading state (edit mode)
    if (isEditMode && loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading property...</p>
                </div>
            </div>
        );
    }

    return (
        <form className="p-4 bg-gray-100 rounded max-w-3xl mx-auto" onSubmit={handleSubmit}>
            {/* Header */}
            <div className="mb-4">
                <h3 className="text-2xl font-bold">
                    {isEditMode ? '✏️ Edit Property' : '📝 Submit a Property'}
                </h3>

                {isEditMode && (
                    <p className="text-sm text-gray-600 mt-1">
                        Property ID: <span className="font-mono text-xs">{id}</span>
                    </p>
                )}
            </div>

            {/* Non-admin info banner (edit mode only) */}
            {isEditMode && !isAdmin && (
                <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded mb-4 text-sm">
                    ℹ️ Your changes will be sent for approval. The property will go back to <strong>Pending</strong> status until approved.
                </div>
            )}

            {/* Success/Error messages */}
            {message && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded mb-4">
                    ✅ {message}
                </div>
            )}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded mb-4">
                    ❌ {error}
                </div>
            )}

            {/* Basic Details Section */}
            <BasicDetails
                formData={formData}
                handleInputChange={handleInputChange}
                handleLocationSelect={handleLocationSelect}
            />

            {/* Office Details Section */}
            {isOfficeType() && (
                <OfficeDetails
                    formData={formData}
                    handleInputChange={handleInputChange}
                />
            )}

            {/* Warehouse Details Section */}
            {isWarehouseType() && (
                <WarehouseDetails
                    formData={formData}
                    handleInputChange={handleInputChange}
                />
            )}

            {/* Financial Details Section */}
            <FinancialDetails
                formData={formData}
                handleInputChange={handleInputChange}
            />

            {/* Landlord Details Section */}
            <LandlordDetails
                formData={formData}
                handleInputChange={handleInputChange}
            />

            {/* Highlights */}
            <div className="mb-4">
                <label className="block font-bold">Highlights</label>
                <textarea
                    name="highlights"
                    value={formData.highlights}
                    onChange={handleInputChange}
                    placeholder="e.g., Close to metro, premium building"
                    className="border p-2 w-full"
                    rows="2"
                />
            </div>

            {/* Treta Exclusive */}
            <div className="mb-4">
                <label className="block font-bold">
                    <input
                        type="checkbox"
                        name="is_treta_exclusive"
                        checked={formData.is_treta_exclusive}
                        onChange={handleInputChange}
                        className="mr-2"
                    />
                    Treta Exclusive
                </label>
            </div>

            {/* Photo Upload */}
            <PhotoUpload
                photo={photo}
                handlePhotoChange={handlePhotoChange}
            />

            {/* Action buttons */}
            <div className="flex gap-3 mt-6">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 p-3 rounded font-medium"
                    disabled={saving}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="flex-1 bg-teal-600 hover:bg-teal-700 text-white p-3 rounded font-medium disabled:opacity-50"
                    disabled={saving}
                >
                    {saving
                        ? '⏳ Saving...'
                        : isEditMode
                            ? '💾 Save Changes'
                            : '📤 Submit Property'
                    }
                </button>
            </div>
        </form>
    );
}

export default PropertyForm;